import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { useToast } from '../../hooks/use-toast';
import { Save, RefreshCw, Facebook, Instagram, Twitter, Linkedin, Youtube, Music } from 'lucide-react';

const SocialMediaManager = () => {
  const [links, setLinks] = useState({
    facebook: '',
    instagram: '',
    twitter: '',
    linkedin: '',
    youtube: '',
    tiktok: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

  useEffect(() => {
    fetchLinks();
  }, []);

  const fetchLinks = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}/api/social-media-links`);

      if (response.ok) {
        const data = await response.json();
        setLinks({
          facebook: data.facebook || '',
          instagram: data.instagram || '',
          twitter: data.twitter || '',
          linkedin: data.linkedin || '',
          youtube: data.youtube || '',
          tiktok: data.tiktok || ''
        });
      }
    } catch (error) {
      console.error('Error fetching social media links:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch social media links',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/admin/social-media-links`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(links)
      });

      if (response.ok) {
        toast({
          title: 'Success',
          description: 'Social media links updated successfully'
        });
      } else {
        toast({
          title: 'Error',
          description: 'Failed to update social media links',
          variant: 'destructive'
        });
      }
    } catch (error) {
      console.error('Error updating links:', error);
      toast({
        title: 'Error',
        description: 'Failed to update social media links',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (platform, value) => {
    setLinks({
      ...links,
      [platform]: value
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold dark:text-gray-100">Social Media Links</h2>
          <p className="text-gray-600 dark:text-gray-400 text-sm mt-1">
            Manage your social media profile links
          </p>
        </div>
        <Button onClick={fetchLinks} variant="outline">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      <Card className="dark:bg-gray-800 dark:border-gray-700">
        <CardHeader>
          <CardTitle className="dark:text-gray-100">Social Media Profiles</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Facebook */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              <Facebook className="w-5 h-5 text-blue-600" />
              Facebook
            </label>
            <Input
              type="url"
              value={links.facebook}
              onChange={(e) => handleChange('facebook', e.target.value)}
              placeholder="https://facebook.com/your-page"
              className="dark:bg-gray-700 dark:border-gray-600"
            />
          </div>

          {/* Instagram */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              <Instagram className="w-5 h-5 text-pink-600" />
              Instagram
            </label>
            <Input
              type="url"
              value={links.instagram}
              onChange={(e) => handleChange('instagram', e.target.value)}
              placeholder="https://instagram.com/your-profile"
              className="dark:bg-gray-700 dark:border-gray-600"
            />
          </div>

          {/* Twitter */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              <Twitter className="w-5 h-5 text-blue-400" />
              Twitter / X
            </label>
            <Input
              type="url"
              value={links.twitter}
              onChange={(e) => handleChange('twitter', e.target.value)}
              placeholder="https://twitter.com/your-handle"
              className="dark:bg-gray-700 dark:border-gray-600"
            />
          </div>

          {/* LinkedIn */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              <Linkedin className="w-5 h-5 text-blue-700" />
              LinkedIn
            </label>
            <Input
              type="url"
              value={links.linkedin}
              onChange={(e) => handleChange('linkedin', e.target.value)}
              placeholder="https://linkedin.com/company/your-company"
              className="dark:bg-gray-700 dark:border-gray-600"
            />
          </div>

          {/* YouTube */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              <Youtube className="w-5 h-5 text-red-600" />
              YouTube
            </label>
            <Input
              type="url"
              value={links.youtube}
              onChange={(e) => handleChange('youtube', e.target.value)}
              placeholder="https://youtube.com/@your-channel"
              className="dark:bg-gray-700 dark:border-gray-600"
            />
          </div>

          {/* TikTok */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              <Music className="w-5 h-5 text-gray-900 dark:text-gray-100" />
              TikTok
            </label>
            <Input
              type="url"
              value={links.tiktok}
              onChange={(e) => handleChange('tiktok', e.target.value)}
              placeholder="https://tiktok.com/@your-profile"
              className="dark:bg-gray-700 dark:border-gray-600"
            />
          </div>

          {/* Save Button */}
          <div className="pt-4 border-t dark:border-gray-700">
            <Button onClick={handleSave} disabled={saving} className="w-full">
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>

          {/* Preview */}
          <div className="pt-4 border-t dark:border-gray-700">
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Preview:</p>
            <div className="flex flex-wrap gap-3">
              {links.facebook && (
                <a href={links.facebook} target="_blank" rel="noopener noreferrer" className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg hover:bg-blue-200 dark:hover:bg-blue-800 transition">
                  <Facebook className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                </a>
              )}
              {links.instagram && (
                <a href={links.instagram} target="_blank" rel="noopener noreferrer" className="p-2 bg-pink-100 dark:bg-pink-900 rounded-lg hover:bg-pink-200 dark:hover:bg-pink-800 transition">
                  <Instagram className="w-5 h-5 text-pink-600 dark:text-pink-400" />
                </a>
              )}
              {links.twitter && (
                <a href={links.twitter} target="_blank" rel="noopener noreferrer" className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg hover:bg-blue-200 dark:hover:bg-blue-800 transition">
                  <Twitter className="w-5 h-5 text-blue-400" />
                </a>
              )}
              {links.linkedin && (
                <a href={links.linkedin} target="_blank" rel="noopener noreferrer" className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg hover:bg-blue-200 dark:hover:bg-blue-800 transition">
                  <Linkedin className="w-5 h-5 text-blue-700 dark:text-blue-500" />
                </a>
              )}
              {links.youtube && (
                <a href={links.youtube} target="_blank" rel="noopener noreferrer" className="p-2 bg-red-100 dark:bg-red-900 rounded-lg hover:bg-red-200 dark:hover:bg-red-800 transition">
                  <Youtube className="w-5 h-5 text-red-600 dark:text-red-400" />
                </a>
              )}
              {links.tiktok && (
                <a href={links.tiktok} target="_blank" rel="noopener noreferrer" className="p-2 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition">
                  <Music className="w-5 h-5 text-gray-900 dark:text-gray-100" />
                </a>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SocialMediaManager;
